import { ApiError } from "@ronin/api-client";
import {
  HOME_ICONS,
  ICON_SET_IMAGES,
  isWebUrl,
  type LinkConfig,
  type LinkIcon as LinkIconValue,
} from "@ronin/contracts";
import { Button, cn, Input, Label, SegmentedControl, Tooltip, toast } from "@ronin/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2, Upload } from "lucide-react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { api } from "../../api.js";
import { errorMessage, showError } from "../../lib/errors.js";
import { FormSection } from "./form-section.js";
import { homeIcons } from "./icons.js";
import { LinkIcon } from "./link-icon.js";

const IMAGE_TYPES = "image/png,image/jpeg,image/gif,image/webp";

export const newLink = (): LinkConfig => ({
  name: "",
  url: "",
  icon: { kind: "lucide", name: "globe" },
});

/** FR7: a valid link has a name and an http(s) address. */
export const isValidLink = (link: LinkConfig) =>
  link.name.trim() !== "" && isWebUrl(link.url.trim()) && link.url.trim().length <= 2048;

/** spec14 FR5: a container tile's address may be empty (then the tile is not clickable). */
export const isValidContainerLink = (link: LinkConfig) =>
  link.name.trim() !== "" && (link.url.trim() === "" || isValidLink(link));

/**
 * spec12 FR7, FR11: a link's name and address on the left, its icon on the right (stacked on
 * narrow screens). `children` adds more details below the address.
 */
export function LinkFields({
  value,
  onChange,
  urlOptional = false,
  children,
}: {
  value: LinkConfig;
  onChange: (link: LinkConfig) => void;
  /** spec14: container tiles work without an address. */
  urlOptional?: boolean;
  children?: ReactNode;
}) {
  const id = useId();
  const [urlTouched, setUrlTouched] = useState(false);
  const set = <K extends keyof LinkConfig>(key: K, next: LinkConfig[K]) =>
    onChange({ ...value, [key]: next });
  const url = value.url.trim();
  const urlInvalid = urlTouched && url !== "" && !(isWebUrl(url) && url.length <= 2048);

  return (
    <div className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
      <FormSection title="Details">
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${id}-name`}>Name</Label>
          <Input
            id={`${id}-name`}
            value={value.name}
            maxLength={60}
            placeholder="e.g. Jellyfin"
            data-autofocus
            onChange={(e) => set("name", e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${id}-url`}>Address</Label>
          <Input
            id={`${id}-url`}
            value={value.url}
            type="url"
            inputMode="url"
            maxLength={2048}
            placeholder="https://media.example.com"
            aria-invalid={urlInvalid}
            aria-describedby={`${id}-url-hint`}
            onBlur={() => setUrlTouched(true)}
            onChange={(e) => set("url", e.target.value)}
          />
          <p
            id={`${id}-url-hint`}
            className={cn("text-xs", urlInvalid ? "text-destructive" : "text-faint-foreground")}
          >
            {urlInvalid
              ? "Use an address starting with http:// or https://"
              : urlOptional
                ? "Optional: opened when you click the tile."
                : "Links are stored as plain text."}
          </p>
        </div>
        {children}
      </FormSection>
      <FormSection title="Icon">
        <IconPicker
          icon={value.icon}
          suggestion={value.name}
          onChange={(icon) => set("icon", icon)}
        />
      </FormSection>
    </div>
  );
}

type Source = "service" | "lucide" | "image";

function IconPicker({
  icon,
  suggestion,
  onChange,
}: {
  icon: LinkIconValue;
  /** The link's name: the first search for a service icon. */
  suggestion: string;
  onChange: (icon: LinkIconValue) => void;
}) {
  const id = useId();
  const [kind, setKind] = useState<Source>(icon.kind === "image" ? "image" : "lucide");
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const terms = search.trim().toLowerCase();
  const icons = HOME_ICONS.filter((name) => name.includes(terms));

  async function upload(file: File) {
    setUploading(true);
    try {
      const attachment = await api.attachments.upload(file, file.name);
      if (!attachment.inline) {
        toast.error("Icons must be PNG, JPEG, GIF or WebP images.");
        return;
      }
      onChange({ kind: "image", attachmentId: attachment.id });
    } catch (error) {
      toast.error(
        error instanceof ApiError
          ? errorMessage(error, file.name || "the image", "uploaded")
          : `Could not upload ${file.name || "the image"}.`,
      );
    } finally {
      setUploading(false);
    }
  }

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="sr-only">Icon</legend>
      <div className="flex items-center gap-3">
        <span className="flex size-11 items-center justify-center border bg-inset">
          <LinkIcon icon={icon} className="size-7 text-secondary-foreground" />
        </span>
        <SegmentedControl
          aria-label="Icon source"
          value={kind}
          onValueChange={setKind}
          options={[
            { value: "service", label: "Service" },
            { value: "lucide", label: "Ronin icon" },
            { value: "image", label: "Image" },
          ]}
        />
      </div>

      {/* One fixed height for every source, so switching never resizes the dialog. */}
      <div className="flex h-72 min-h-0 flex-col gap-2">
        {kind === "service" ? (
          <ServiceIcons suggestion={suggestion} onPick={onChange} />
        ) : kind === "lucide" ? (
          <>
            <Label htmlFor={`${id}-search`} className="sr-only">
              Search icons
            </Label>
            <Input
              id={`${id}-search`}
              value={search}
              placeholder="Search icons"
              onChange={(e) => setSearch(e.target.value)}
            />
            <div
              role="radiogroup"
              aria-label="Icons"
              className="grid min-h-0 flex-1 auto-rows-min grid-cols-[repeat(auto-fill,minmax(2.5rem,1fr))] gap-1 overflow-y-auto"
            >
              {icons.map((name) => {
                const Icon = homeIcons[name];
                const checked = icon.kind === "lucide" && icon.name === name;
                return (
                  <Tooltip key={name} content={name.replaceAll("-", " ")}>
                    {/* biome-ignore lint/a11y/useSemanticElements: a grid of icon buttons, not native radios */}
                    <button
                      type="button"
                      role="radio"
                      aria-checked={checked}
                      aria-label={name.replaceAll("-", " ")}
                      onClick={() => onChange({ kind: "lucide", name })}
                      className={cn(
                        "flex size-10 items-center justify-center border border-transparent text-secondary-foreground hover:bg-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
                        checked && "border-ring bg-accent text-brand",
                      )}
                    >
                      <Icon className="size-5" aria-hidden />
                    </button>
                  </Tooltip>
                );
              })}
              {icons.length === 0 && (
                <p className="col-span-full py-2 text-xs text-muted-foreground">No icon matches.</p>
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-1">
            <input
              ref={fileInput}
              type="file"
              accept={IMAGE_TYPES}
              className="sr-only"
              tabIndex={-1}
              aria-hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) void upload(file);
              }}
            />
            <Button
              type="button"
              variant="secondary"
              className="w-fit"
              disabled={uploading}
              onClick={() => fileInput.current?.click()}
            >
              {uploading ? <Loader2 className="animate-spin" /> : <Upload />}
              {icon.kind === "image" ? "Replace image" : "Upload image"}
            </Button>
            <p className="text-xs text-faint-foreground">PNG, JPEG, GIF or WebP, up to 25 MB.</p>
          </div>
        )}
      </div>
    </fieldset>
  );
}

/**
 * spec12 FR11: icons of self-hosted services (dashboard-icons). The server searches the set
 * and copies the chosen icon into the user's attachments; the browser only ever loads it
 * from Ronin, so the CSP stays `img-src 'self'`.
 */
function ServiceIcons({
  suggestion,
  onPick,
}: {
  suggestion: string;
  onPick: (icon: LinkIconValue) => void;
}) {
  const id = useId();
  const [search, setSearch] = useState(suggestion.trim());
  const query = useDebounced(search.trim(), 250);
  const results = useQuery({
    queryKey: ["home", "icons", query],
    queryFn: () => api.home.searchIcons(query),
    enabled: query.length >= 2,
    staleTime: Number.POSITIVE_INFINITY,
  });
  const pick = useMutation({
    mutationFn: (name: string) => api.home.importIcon(name),
    onSuccess: (attachment) => onPick({ kind: "image", attachmentId: attachment.id }),
    onError: showError("the icon", "added"),
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <Label htmlFor={`${id}-service`} className="sr-only">
        Search service icons
      </Label>
      <Input
        id={`${id}-service`}
        value={search}
        placeholder="Search services, e.g. jellyfin"
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="flex min-h-0 flex-1 flex-wrap content-start gap-1 overflow-y-auto">
        {query.length < 2 && (
          <p className="text-xs text-muted-foreground">Type at least two letters.</p>
        )}
        {results.isFetching && (
          <p className="flex items-center gap-2 text-xs text-dim-foreground">
            <Loader2 className="size-3 animate-spin" aria-hidden />
            Searching …
          </p>
        )}
        {results.isError && (
          <p className="text-xs text-destructive">
            {errorMessage(results.error, "the search", "loaded")}
          </p>
        )}
        {results.data?.length === 0 && (
          <p className="text-xs text-muted-foreground">No service icon matches.</p>
        )}
        {results.data?.map((name) => (
          <Tooltip key={name} content={name}>
            <button
              type="button"
              disabled={pick.isPending}
              onClick={() => pick.mutate(name)}
              className="flex h-16 w-20 flex-col items-center justify-center gap-1 border border-input px-1 text-[11px] text-secondary-foreground transition-colors duration-150 hover:bg-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
            >
              {pick.isPending && pick.variables === name ? (
                <Loader2 className="size-6 animate-spin text-dim-foreground" aria-hidden />
              ) : (
                // Preview straight from the pinned icon set (CSP img-src allows only its folder);
                // the chosen icon is copied into Ronin.
                <img
                  src={`${ICON_SET_IMAGES}${name}.webp`}
                  alt=""
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  draggable={false}
                  className="size-6 object-contain"
                />
              )}
              <span className="w-full truncate text-center">{name}</span>
            </button>
          </Tooltip>
        ))}
      </div>
      <p className="text-xs text-faint-foreground">
        From the dashboard-icons set (Apache-2.0); the icon is saved in your files.
      </p>
    </div>
  );
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}
